<script setup>
import { ref, computed, watch, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import ProgressBar from 'dashboard/components-next/progress/ProgressBar.vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import PipelineAPI from '../../../../../../api/pipelineCards';
import Modal from 'dashboard/components/Modal.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import { usePipelinePermissions } from '../../../composables/usePipelinePermissions';
import { findPipelineForCard } from '../../../helpers/pipelineStageResolver';
import { trustedAttachmentUrl } from '../../../helpers/attachmentUrl';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  notes: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['itemUpdated', 'goToTab']);

const { t } = useI18n();
const { showAlert } = useAlert();
const store = useStore();
const selectedPipeline = computed(
  () => store.getters['pipelines/getSelectedPipeline']
);
const pipelines = computed(() => store.getters['pipelines/getPipelines'] || []);
const itemPipeline = computed(() =>
  findPipelineForCard(
    pipelines.value,
    selectedPipeline.value,
    props.item?.pipeline_id
  )
);
const { canEditCard } = usePipelinePermissions(itemPipeline);

const MAX_ATTACHMENT_SIZE_BYTES = 25 * 1024 * 1024;
const ALLOWED_ATTACHMENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'application/pdf',
  'text/plain',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'application/x-zip-compressed',
];
const acceptedAttachmentTypes = ALLOWED_ATTACHMENT_TYPES.join(',');

// Upload modal state
const showUploadModal = ref(false);
const uploadProgress = ref(0);
const isUploading = ref(false);
const uploadError = ref('');
const uploadSuccess = ref(false);
const isDragging = ref(false);
const selectedFile = ref(null);
const busyAttachmentKey = ref(null);
const deleteConfirmationRef = ref(null);
const isConfirmingDelete = ref(false);
let uploadController = null;
let downloadController = null;
let deleteController = null;
let successCloseTimer = null;
let contextGeneration = 0;
let uploadGeneration = 0;
let downloadGeneration = 0;
let deleteGeneration = 0;
let componentUnmounted = false;

const normalizePositiveId = value => {
  const normalizedValue = String(value ?? '').trim();
  return /^[1-9]\d*$/.test(normalizedValue) ? normalizedValue : '';
};
const currentAttachmentContext = () => ({
  accountId: normalizePositiveId(store.getters.getCurrentAccountId),
  cardId: normalizePositiveId(props.item?.id),
  generation: contextGeneration,
});
const attachmentContextMatches = context => {
  const currentContext = currentAttachmentContext();
  return (
    !componentUnmounted &&
    currentContext.generation === context.generation &&
    currentContext.accountId === context.accountId &&
    currentContext.cardId === context.cardId
  );
};

const attachmentUrl = attachment =>
  attachment.url || attachment.attachment_url || attachment.file_url || '';
const attachmentName = attachment =>
  attachment.filename ||
  attachment.fileName ||
  attachment.file_name ||
  t('PIPELINE.ATTACHMENTS_LABEL');
const attachmentSize = attachment =>
  attachment.byte_size || attachment.fileSize || attachment.file_size;
const attachmentKey = attachment => {
  const sourceType = attachment.source?.type || 'unknown';
  const url = attachmentUrl(attachment);
  if (url) return `url:${url}`;
  if (attachment.id !== undefined && attachment.id !== null) {
    return `${sourceType}:${attachment.id}`;
  }
  return `${sourceType}:${attachmentName(attachment)}:${attachmentSize(attachment) || 0}`;
};
const canDeleteAttachment = attachment =>
  canEditCard.value &&
  ['card', 'note'].includes(attachment.source?.type) &&
  !!normalizePositiveId(attachment?.id);

const getAllAttachments = computed(() => {
  const noteAttachmentsList = props.notes.reduce((attachments, note) => {
    if (note.attachments && Array.isArray(note.attachments)) {
      return [
        ...attachments,
        ...note.attachments.map(attachment => ({
          ...attachment,
          filename: attachmentName(attachment),
          url: attachmentUrl(attachment),
          source: {
            type: 'note',
            id: note.id,
            date: note.created_at,
            text:
              (note.text?.substring(0, 50) || '') +
              ((note.text?.length || 0) > 50 ? '...' : ''),
          },
        })),
      ];
    }
    return attachments;
  }, []);

  const itemDetailsAttachments =
    props.item.item_details?.attachments?.map(attachment => ({
      ...attachment,
      filename: attachmentName(attachment),
      url: attachmentUrl(attachment),
      byte_size: attachmentSize(attachment),
      source: {
        type: 'legacy',
        id: props.item.id,
      },
    })) || [];

  const activeStorageAttachments =
    props.item.attachments?.map(attachment => ({
      id: attachment.id,
      filename: attachment.filename,
      fileName: attachment.filename,
      content_type: attachment.content_type,
      byte_size: attachment.byte_size,
      fileSize: attachment.byte_size,
      url: attachment.url,
      source: {
        type: 'card',
        id: props.item.id,
      },
    })) || [];

  const allAttachments = [
    ...noteAttachmentsList,
    ...activeStorageAttachments,
    ...itemDetailsAttachments,
  ];
  const seen = new Set();
  return allAttachments.filter(att => {
    const key = attachmentKey(att);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
});

const formatFileSize = bytes => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / k ** i).toFixed(2)) + ' ' + sizes[i];
};

const openUploadModal = () => {
  if (!canEditCard.value) return;
  showUploadModal.value = true;
  uploadProgress.value = 0;
  uploadError.value = '';
  uploadSuccess.value = false;
  selectedFile.value = null;
};

const closeUploadModal = () => {
  uploadGeneration += 1;
  uploadController?.abort();
  uploadController = null;
  if (successCloseTimer) clearTimeout(successCloseTimer);
  successCloseTimer = null;
  showUploadModal.value = false;
  uploadProgress.value = 0;
  uploadError.value = '';
  uploadSuccess.value = false;
  selectedFile.value = null;
  isDragging.value = false;
  // Closing bumps uploadGeneration, so the in-flight request's finally no longer
  // matches and cannot clear isUploading; reset it here or the button stays
  // disabled until the component is remounted.
  isUploading.value = false;
};

const handleDragEnter = e => {
  e.preventDefault();
  isDragging.value = true;
};

const handleDragLeave = e => {
  e.preventDefault();
  isDragging.value = false;
};

const handleDragOver = e => {
  e.preventDefault();
};

const setSelectedFile = file => {
  if (!canEditCard.value) return;
  uploadError.value = '';
  if (!file) {
    selectedFile.value = null;
    return;
  }
  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    selectedFile.value = null;
    uploadError.value = t('PIPELINE.MESSAGES.ATTACHMENT_TOO_LARGE');
    return;
  }
  const attachmentMimeType = file.type?.trim();
  if (
    !attachmentMimeType ||
    !ALLOWED_ATTACHMENT_TYPES.includes(attachmentMimeType)
  ) {
    selectedFile.value = null;
    uploadError.value = t('PIPELINE.MESSAGES.ATTACHMENT_TYPE_NOT_ALLOWED');
    return;
  }
  selectedFile.value = file;
};

const handleDrop = e => {
  e.preventDefault();
  isDragging.value = false;
  if (!canEditCard.value) return;
  const files = e.dataTransfer.files;
  if (files.length > 0) {
    setSelectedFile(files[0]);
  }
};

const handleFileSelect = event => {
  if (!canEditCard.value) return;
  const file = event.target.files[0];
  setSelectedFile(file);
};

const uploadSelectedFile = async () => {
  if (!canEditCard.value || !selectedFile.value || isUploading.value) return;

  const uploadContext = currentAttachmentContext();
  if (!uploadContext.accountId || !uploadContext.cardId) return;
  const fileSnapshot = selectedFile.value;
  uploadGeneration += 1;
  const requestGeneration = uploadGeneration;

  isUploading.value = true;
  uploadError.value = '';
  uploadProgress.value = 0;

  const controller = new AbortController();
  uploadController = controller;

  try {
    await PipelineAPI.uploadAttachment(uploadContext.cardId, fileSnapshot, {
      accountId: uploadContext.accountId,
      signal: controller.signal,
      onUploadProgress: progressEvent => {
        if (
          !progressEvent.total ||
          controller.signal.aborted ||
          requestGeneration !== uploadGeneration ||
          !attachmentContextMatches(uploadContext)
        ) {
          return;
        }
        uploadProgress.value = Math.min(
          99,
          Math.round((progressEvent.loaded / progressEvent.total) * 100)
        );
      },
    });
    if (
      controller.signal.aborted ||
      requestGeneration !== uploadGeneration ||
      !attachmentContextMatches(uploadContext) ||
      !canEditCard.value
    ) {
      return;
    }
    uploadProgress.value = 100;
    uploadSuccess.value = true;

    emit('itemUpdated');
    emit('goToTab', 'attachments');

    successCloseTimer = setTimeout(() => {
      if (
        requestGeneration === uploadGeneration &&
        attachmentContextMatches(uploadContext)
      ) {
        closeUploadModal();
      }
    }, 1500);
  } catch (error) {
    if (
      controller.signal.aborted ||
      requestGeneration !== uploadGeneration ||
      !attachmentContextMatches(uploadContext)
    ) {
      return;
    }
    uploadError.value =
      error.response?.data?.error ||
      error.message ||
      t('PIPELINE.MESSAGES.UPLOAD_ERROR');
    uploadProgress.value = 0;
  } finally {
    if (
      requestGeneration === uploadGeneration &&
      attachmentContextMatches(uploadContext)
    ) {
      isUploading.value = false;
    }
    if (uploadController === controller) uploadController = null;
  }
};

const clickDownloadLink = (href, filename, { openInNewTab = false } = {}) => {
  const link = document.createElement('a');
  link.href = href;
  link.download = filename;
  if (openInNewTab) {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const downloadFromDirectUrl = attachment => {
  const safeUrl = trustedAttachmentUrl(attachment);
  if (!safeUrl) return false;

  clickDownloadLink(safeUrl, attachmentName(attachment), {
    openInNewTab: true,
  });
  return true;
};

const handleAttachmentDownload = async attachment => {
  if (busyAttachmentKey.value) return;
  const downloadContext = currentAttachmentContext();
  if (!downloadContext.accountId || !downloadContext.cardId) return;
  const attachmentSnapshot = {
    ...attachment,
    source: attachment?.source ? { ...attachment.source } : undefined,
  };
  const targetAttachmentKey = attachmentKey(attachmentSnapshot);
  downloadGeneration += 1;
  const requestGeneration = downloadGeneration;
  const controller = new AbortController();
  downloadController = controller;
  busyAttachmentKey.value = targetAttachmentKey;
  try {
    if (attachmentSnapshot.source?.type === 'card') {
      const attachmentId = normalizePositiveId(attachmentSnapshot.id);
      if (!attachmentId) throw new Error('Attachment ID is unavailable');
      const { data } = await PipelineAPI.downloadAttachment(
        downloadContext.cardId,
        attachmentId,
        {
          accountId: downloadContext.accountId,
          signal: controller.signal,
        }
      );
      if (
        controller.signal.aborted ||
        requestGeneration !== downloadGeneration ||
        !attachmentContextMatches(downloadContext)
      ) {
        return;
      }
      const url = window.URL.createObjectURL(new Blob([data]));
      clickDownloadLink(url, attachmentName(attachmentSnapshot));
      window.URL.revokeObjectURL(url);
    } else {
      if (!attachmentContextMatches(downloadContext)) return;
      if (!downloadFromDirectUrl(attachmentSnapshot)) {
        showAlert(t('PIPELINE.MESSAGES.ATTACHMENT_DOWNLOAD_ERROR'), 'error');
      }
    }
  } catch (error) {
    if (
      controller.signal.aborted ||
      requestGeneration !== downloadGeneration ||
      !attachmentContextMatches(downloadContext)
    ) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error('[AttachmentsTab] handleAttachmentDownload failed:', error);
    showAlert(t('PIPELINE.MESSAGES.ATTACHMENT_DOWNLOAD_ERROR'), 'error');
  } finally {
    if (
      requestGeneration === downloadGeneration &&
      attachmentContextMatches(downloadContext) &&
      busyAttachmentKey.value === targetAttachmentKey
    ) {
      busyAttachmentKey.value = null;
    }
    if (downloadController === controller) downloadController = null;
  }
};

const attachmentIdentity = attachment =>
  `${attachment?.source?.type || ''}:${normalizePositiveId(
    attachment?.source?.id
  )}:${normalizePositiveId(attachment?.id)}`;

const handleAttachmentDelete = async attachment => {
  if (
    !canDeleteAttachment(attachment) ||
    busyAttachmentKey.value ||
    isConfirmingDelete.value
  ) {
    return;
  }

  const deleteContext = currentAttachmentContext();
  const attachmentSnapshot = {
    ...attachment,
    source: attachment?.source ? { ...attachment.source } : undefined,
  };
  const targetIdentity = attachmentIdentity(attachmentSnapshot);
  if (
    !deleteContext.accountId ||
    !deleteContext.cardId ||
    !normalizePositiveId(attachmentSnapshot.id)
  ) {
    return;
  }

  isConfirmingDelete.value = true;
  let confirmed = false;
  try {
    confirmed = await deleteConfirmationRef.value?.showConfirmation();
  } finally {
    isConfirmingDelete.value = false;
  }
  const attachmentStillCurrent = getAllAttachments.value.some(
    currentAttachment =>
      attachmentIdentity(currentAttachment) === targetIdentity
  );
  if (
    !confirmed ||
    !attachmentContextMatches(deleteContext) ||
    !canDeleteAttachment(attachmentSnapshot) ||
    !attachmentStillCurrent ||
    busyAttachmentKey.value
  ) {
    return;
  }

  deleteGeneration += 1;
  const requestGeneration = deleteGeneration;
  const controller = new AbortController();
  deleteController = controller;
  const targetAttachmentKey = attachmentKey(attachmentSnapshot);
  busyAttachmentKey.value = targetAttachmentKey;

  try {
    const requestOptions = {
      accountId: deleteContext.accountId,
      signal: controller.signal,
    };
    if (attachmentSnapshot.source.type === 'note') {
      await PipelineAPI.deleteNoteAttachment(
        deleteContext.cardId,
        normalizePositiveId(attachmentSnapshot.id),
        requestOptions
      );
    } else {
      await PipelineAPI.deleteAttachment(
        deleteContext.cardId,
        normalizePositiveId(attachmentSnapshot.id),
        requestOptions
      );
    }
    if (
      controller.signal.aborted ||
      requestGeneration !== deleteGeneration ||
      !attachmentContextMatches(deleteContext) ||
      !canEditCard.value
    ) {
      return;
    }
    emit('itemUpdated');
  } catch (error) {
    if (
      controller.signal.aborted ||
      requestGeneration !== deleteGeneration ||
      !attachmentContextMatches(deleteContext)
    ) {
      return;
    }
    // eslint-disable-next-line no-console
    console.error('[AttachmentsTab] handleAttachmentDelete failed:', error);
    showAlert(t('PIPELINE.MESSAGES.ATTACHMENT_DELETE_ERROR'), 'error');
  } finally {
    if (
      requestGeneration === deleteGeneration &&
      attachmentContextMatches(deleteContext) &&
      busyAttachmentKey.value === targetAttachmentKey
    ) {
      busyAttachmentKey.value = null;
    }
    if (deleteController === controller) deleteController = null;
  }
};

watch(
  () => [
    normalizePositiveId(store.getters.getCurrentAccountId),
    normalizePositiveId(props.item?.id),
  ],
  (context, previousContext) => {
    if (!previousContext) return;
    // The getter returns a fresh array each run, so Vue fires this watcher on
    // any reactive re-evaluation. Only reset in-flight state when the account
    // or card id actually changed — replacing `item` with a refreshed object
    // carrying the same id must not abort an ongoing upload/download.
    if (
      context[0] === previousContext[0] &&
      context[1] === previousContext[1]
    ) {
      return;
    }
    contextGeneration += 1;
    uploadGeneration += 1;
    downloadGeneration += 1;
    deleteGeneration += 1;
    uploadController?.abort();
    downloadController?.abort();
    deleteController?.abort();
    uploadController = null;
    downloadController = null;
    deleteController = null;
    if (successCloseTimer) clearTimeout(successCloseTimer);
    successCloseTimer = null;
    showUploadModal.value = false;
    uploadProgress.value = 0;
    uploadError.value = '';
    uploadSuccess.value = false;
    selectedFile.value = null;
    isDragging.value = false;
    isUploading.value = false;
    busyAttachmentKey.value = null;
  }
);

onUnmounted(() => {
  componentUnmounted = true;
  contextGeneration += 1;
  uploadGeneration += 1;
  downloadGeneration += 1;
  deleteGeneration += 1;
  uploadController?.abort();
  downloadController?.abort();
  deleteController?.abort();
  if (successCloseTimer) clearTimeout(successCloseTimer);
});
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between mb-4">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ t('PIPELINE.ATTACHMENTS_LABEL') }}
      </h3>
      <button
        v-if="canEditCard"
        type="button"
        class="inline-flex min-h-11 items-center gap-1.5 px-3 py-2 text-xs font-medium text-n-brand bg-n-alpha-1 hover:bg-n-alpha-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
        @click="openUploadModal"
      >
        <fluent-icon icon="add" size="14" />
        {{ t('PIPELINE.ADD_ATTACHMENT') }}
      </button>
    </div>
    <div class="grid grid-cols-1 gap-3">
      <div
        v-for="attachment in getAllAttachments"
        :key="attachmentKey(attachment)"
        :data-attachment-source="attachment.source?.type"
        class="p-3 bg-n-solid-3 rounded-lg"
      >
        <div class="flex items-center gap-3">
          <fluent-icon
            icon="document"
            size="20"
            class="text-n-slate-10 flex-shrink-0"
          />
          <div class="flex-1 min-w-0">
            <p class="text-sm font-medium text-n-slate-12 truncate">
              {{ attachmentName(attachment) }}
            </p>
            <p class="text-xs text-n-slate-10">
              {{ formatFileSize(attachmentSize(attachment)) }}
            </p>
          </div>
          <div class="flex items-center gap-1">
            <button
              type="button"
              class="min-h-11 min-w-11 rounded p-2 text-n-slate-10 hover:text-n-brand hover:bg-n-alpha-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
              :title="t('PIPELINE.DOWNLOAD_ATTACHMENT')"
              :aria-label="t('PIPELINE.DOWNLOAD_ATTACHMENT')"
              :disabled="busyAttachmentKey === attachmentKey(attachment)"
              @click="handleAttachmentDownload(attachment)"
            >
              <fluent-icon icon="arrow-download" size="16" />
            </button>
            <button
              v-if="canDeleteAttachment(attachment)"
              type="button"
              class="min-h-11 min-w-11 rounded p-2 text-n-slate-10 hover:text-n-ruby-11 hover:bg-n-ruby-3 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50"
              :title="t('PIPELINE.DELETE_ATTACHMENT')"
              :aria-label="t('PIPELINE.DELETE_ATTACHMENT')"
              :disabled="busyAttachmentKey === attachmentKey(attachment)"
              @click="handleAttachmentDelete(attachment)"
            >
              <fluent-icon icon="delete" size="16" />
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="getAllAttachments.length === 0" class="text-center py-8">
      <fluent-icon
        icon="attach"
        size="32"
        class="text-n-slate-9 mx-auto mb-2"
      />
      <p class="text-sm text-n-slate-10">
        {{ t('PIPELINE.ATTACHMENTS_EMPTY') }}
      </p>
    </div>

    <!-- Upload Modal -->
    <Modal
      v-if="canEditCard"
      v-model:show="showUploadModal"
      @close="closeUploadModal"
    >
      <div class="p-6 max-w-md mx-auto">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-heading-2 text-n-slate-12">
            {{ t('PIPELINE.UPLOAD_FILE') }}
          </h3>
          <button
            type="button"
            class="min-h-11 min-w-11 rounded p-2 hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            :aria-label="t('PIPELINE.CLOSE_MODAL')"
            @click="closeUploadModal"
          >
            <fluent-icon icon="dismiss" size="20" class="text-n-slate-10" />
          </button>
        </div>

        <!-- Drag & Drop Zone -->
        <div
          class="border-2 border-dashed rounded-lg p-8 text-center transition-colors"
          :class="isDragging ? 'border-n-brand bg-n-alpha-1' : 'border-n-weak'"
          @dragenter="handleDragEnter"
          @dragleave="handleDragLeave"
          @dragover="handleDragOver"
          @drop="handleDrop"
        >
          <fluent-icon
            icon="arrow-upload"
            size="48"
            class="mx-auto mb-4"
            :class="isDragging ? 'text-n-brand' : 'text-n-slate-10'"
          />
          <p class="text-sm text-n-slate-11 mb-2">
            {{ t('PIPELINE.DRAG_DROP_FILE') }}
          </p>
          <p class="text-xs text-n-slate-10 mb-4">
            {{ t('PIPELINE.OR') }}
          </p>
          <label class="cursor-pointer">
            <input
              type="file"
              name="pipeline-attachment"
              class="hidden"
              :accept="acceptedAttachmentTypes"
              :disabled="!canEditCard"
              @change="handleFileSelect"
            />
            <span
              class="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-n-brand hover:brightness-110 rounded-lg transition-colors"
            >
              <fluent-icon icon="folder" size="16" />
              {{ t('PIPELINE.SELECT_FILE') }}
            </span>
          </label>
        </div>

        <!-- Selected File Info -->
        <div v-if="selectedFile" class="mt-4 p-3 bg-n-solid-3 rounded-lg">
          <div class="flex items-center gap-3">
            <fluent-icon icon="document" size="20" class="text-n-slate-10" />
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ selectedFile.name }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{ formatFileSize(selectedFile.size) }}
              </p>
            </div>
            <button
              type="button"
              class="min-h-11 min-w-11 rounded p-2 text-n-slate-10 hover:text-n-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
              :aria-label="t('PIPELINE.REMOVE_SELECTED_FILE')"
              @click="selectedFile = null"
            >
              <fluent-icon icon="dismiss" size="16" />
            </button>
          </div>
        </div>

        <!-- Progress Bar -->
        <div v-if="isUploading" class="mt-4">
          <div class="flex items-center justify-between mb-1">
            <span class="text-xs text-n-slate-10">{{
              t('PIPELINE.UPLOADING')
            }}</span>
            <span class="text-xs text-n-slate-10">{{
              `${uploadProgress}%`
            }}</span>
          </div>
          <ProgressBar
            :value="uploadProgress"
            :label="t('PIPELINE.UPLOADING')"
          />
        </div>

        <!-- Success Message -->
        <div
          v-if="uploadSuccess"
          class="mt-4 p-3 bg-n-teal-3 rounded-lg flex items-center gap-2"
        >
          <fluent-icon
            icon="checkmark-circle"
            size="20"
            class="text-n-teal-11"
          />
          <span class="text-sm text-n-teal-11">
            {{ t('PIPELINE.UPLOAD_SUCCESS') }}
          </span>
        </div>

        <!-- Error Message -->
        <div
          v-if="uploadError"
          class="mt-4 p-3 bg-n-ruby-3 rounded-lg flex items-center gap-2"
          role="alert"
        >
          <fluent-icon icon="error-circle" size="20" class="text-n-ruby-11" />
          <span class="text-sm text-n-ruby-11">
            {{ uploadError }}
          </span>
        </div>

        <!-- Actions -->
        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            class="min-h-11 px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
            @click="closeUploadModal"
          >
            {{ t('PIPELINE.CANCEL') }}
          </button>
          <button
            type="button"
            class="min-h-11 px-4 py-2 text-sm font-medium text-white bg-n-brand hover:brightness-110 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="!canEditCard || !selectedFile || isUploading"
            @click="uploadSelectedFile"
          >
            {{ isUploading ? t('PIPELINE.UPLOADING') : t('PIPELINE.UPLOAD') }}
          </button>
        </div>
      </div>
    </Modal>

    <ConfirmationModal
      ref="deleteConfirmationRef"
      :title="t('PIPELINE.DELETE_ATTACHMENT')"
      :description="t('PIPELINE.MESSAGES.CONFIRM_DELETE_ATTACHMENT')"
      :confirm-label="t('PIPELINE.DELETE_ATTACHMENT')"
      :cancel-label="t('PIPELINE.CANCEL')"
    />
  </div>
</template>
